import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import api from '../api'
import { ButtonStatus } from '../common/constants'
import CompanyStatusBadge from './CompanyStatusBadge'
import Card from './cards/Card'
import { apiErrorText } from '../utils/apiError'
import Button from './Button'
import Dialog from './dialogs/Dialog'
import Pagination from './Pagination'
import Table from './Table'
import TextInput from './TextInput'
import TooltipWrapper from './TooltipWrapper'
import { Each } from '../common/Each'
import useWindowDimensions from '../common/hooks/useWindowDimensions'
import styles from './CompanyUsers.module.css'

const CompanyUsers = forwardRef((_, ref) => {
  const { width } = useWindowDimensions()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [loadingStudents, setLoadingStudents] = useState(false)
  const [loadingPage, setLoadingPage] = useState(true)
  const [students, setStudents] = useState([])
  const [invites, setInvites] = useState([])
  const [loadingInvites, setLoadingInvites] = useState(false)
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(0)
  const [per] = useState(25)
  const [showPagination, setShowPagination] = useState(true)
  const [q, setQ] = useState(null)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [error, setError] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [inviteStatus, setInviteStatus] = useState(null)

  const isInviteEmailValid = useCallback((email) => {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(String(email || '').trim().toLowerCase())
  }, [])

  const getStudents = useCallback(async (query = null) => {
    setLoadingStudents(true)
    try {
      const params = new URLSearchParams()

      if (query) {
        params.set('q', query)
      } else {
        params.set('page', String(page))
        params.set('per', String(per))
      }

      const response = await api.get(`/company/users?${params.toString()}`)
      setStudents(response?.users || [])
      setCount(response?.count || 0)
    } catch (e) {
      console.error(e)
      setError(apiErrorText(e))
    }
    setLoadingStudents(false)
    setLoadingPage(false)
  }, [page, per])

  const getInvites = useCallback(async () => {
    setLoadingInvites(true)
    try {
      const params = new URLSearchParams()
      params.set('page', '0')
      params.set('per', '25')

      const response = await api.get(`/company/users/invites?${params.toString()}`)
      setInvites(response?.invites || [])
    } catch (e) {
      console.error(e)
      setError(apiErrorText(e))
    }
    setLoadingInvites(false)
  }, [])

  useEffect(() => {
    getStudents(q)
  }, [getStudents, q])

  useEffect(() => {
    getInvites()
  }, [getInvites])

  const onSearch = useCallback((text) => {
    if (text && text.length >= 3) {
      setShowPagination(false)
      setPage(0)
      setQ(text)
      return
    }

    if (!text) {
      setShowPagination(true)
      setPage(0)
      setQ(null)
    }
  }, [])

  const onInviteClick = useCallback(() => {
    setInviteDialogOpen(true)
  }, [])

  useImperativeHandle(ref, () => ({
    onAddClick: onInviteClick,
  }), [onInviteClick])

  const resetInviteDialog = useCallback(() => {
    setInviteDialogOpen(false)
    setInviteEmail('')
    setInviteStatus(null)
    setInviteError('')
  }, [])

  const submitInvite = useCallback(async () => {
    if (inviteStatus === ButtonStatus.Loading || !inviteEmail?.trim() || !isInviteEmailValid(inviteEmail)) {
      return
    }

    setInviteError('')
    setInviteStatus(ButtonStatus.Loading)
    try {
      await api.post('/company/users/invites', { email: inviteEmail.trim() })
      await getInvites()
      setInviteStatus(ButtonStatus.Success)
      setTimeout(() => {
        resetInviteDialog()
      }, 700)
    } catch (e) {
      console.error(e)
      setInviteError(apiErrorText(e))
      setInviteStatus(ButtonStatus.Error)
    }
  }, [getInvites, inviteEmail, inviteStatus, isInviteEmailValid, resetInviteDialog])


  return (
    <div className={styles.container}>
      {error && <div role="alert" className={styles.error}>{error}</div>}
      {invites.length > 0 && (
        <Card appearance="modern" integratedTable title="Inviti in sospeso">
          <Table
            integrated loadingN={6}
            columns={[
              { key: 'email', label: 'EMAIL', type: 'string' },
              { key: 'invited_by_name', label: 'INVIATO DA', type: 'string' },
              { key: 'created_at', label: 'INVIATO IL', type: 'datetime' },
              { key: 'expires_at', label: 'SCADE IL', type: 'datetime' },
              { key: 'status', label: 'STATO', type: 'custom' },
            ]}
            loading={loadingInvites}
            elements={invites.map((invite) => ({
              ...invite,
              invited_by_name: invite?.invited_by_name || '-',
              status: <CompanyStatusBadge status="pending" />,
            }))}
            defaultSort={{ key: 'created_at', direction: 'desc' }}
          />
        </Card>
      )}

      <Card appearance="modern" integratedTable title="Studenti">
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <TextInput appearance="modern"
            onKeyUp={onSearch}
            type='search'
            placeholder={t('search')}
          />
        </div>
      </div>

      <Table
        integrated loadingN={6}
        columns={[
          { key: 'name', label: 'NOME', type: 'string' },
          { key: 'surname', label: 'COGNOME', type: 'string' },
          { key: 'email', label: 'EMAIL', type: 'string' },
          { key: 'status', label: 'STATO', type: 'custom' },
          { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
          { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
          { key: 'courses', label: 'CORSI', type: 'custom' },
        ]}
        loading={loadingStudents}
        elements={students.map((student) => ({
          ...student,
          status: <CompanyStatusBadge status={student.status} />,
          courses: {
            value: student?.courses?.length || 0,
            type: 'number',
            component: student?.courses?.length > 0 ? (
              <TooltipWrapper
                component={
                  <div>
                    <Each of={student.courses} render={(course) => <div>{course.name}</div>} />
                  </div>
                }
              >
                <span style={{ whiteSpace: 'nowrap' }}>{student.courses.length} {student.courses.length === 1 ? 'corso' : 'corsi'}</span>
              </TooltipWrapper>
            ) : (
              <span>Nessun corso</span>
            ),
          },
        }))}
        onRowClick={(student) => {
          if (student?.id) {
            navigate(`/users/company/students/${student.id}`)
          }
        }}
        defaultSort={{ key: 'created_at', direction: 'desc' }}
      />

      {!loadingStudents && !error && students.length === 0 && <p className={styles.emptyState}>Nessuno studente trovato.</p>}
      {showPagination && (
        <div className={styles.pagination}><Pagination total={count} per={per} page={page} group={width > 540 ? 5 : 0} onChange={(p) => setPage(p)} loading={loadingPage} /></div>
      )}

      </Card>

      <Dialog appearance="modern"
        open={inviteDialogOpen}
        title='Invita utente'
        onClose={() => { if (inviteStatus !== ButtonStatus.Loading) resetInviteDialog() }}
        style={{ width: '94vw', maxWidth: '520px' }}
      >
        <div className={styles.dialogBody}>
          <div className={styles.helperText}>
            Inserisci l'email dell'utente. Se non è già associato a un’azienda, verrà creato un invito.
          </div>
          <TextInput appearance="modern"
            value={inviteEmail}
            type='text'
            disabled={inviteStatus === ButtonStatus.Loading}
            placeholder='Email utente'
            error={Boolean(inviteEmail?.trim()) && !isInviteEmailValid(inviteEmail)}
            errorMessage='Inserisci un indirizzo email valido'
            onKeyUp={(value) => {
              setInviteEmail(String(value || ''))
              if (inviteStatus !== null) {
                setInviteStatus(null)
              }
            }}
            onCommit={() => {
              if (inviteEmail?.trim() && isInviteEmailValid(inviteEmail)) {
                submitInvite()
              }
            }}
          />
          {inviteError && <div role="alert" className={styles.error}>{inviteError}</div>}
          <div className={styles.dialogActions}>
            <Button
              style={{ borderRadius: 8 }}
              onClick={submitInvite}
              status={inviteStatus}
              disabled={inviteStatus === ButtonStatus.Loading || !inviteEmail?.trim() || !isInviteEmailValid(inviteEmail)}
            >
              Invita
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
})

export default CompanyUsers
